import { motion, AnimatePresence } from 'framer-motion';
import { X, Package, Building2, Info } from 'lucide-react';
import PropTypes from 'prop-types';
import { useLanguage } from '../contexts/LanguageContext';
import config from '../config/index.js';

const ProductDetailsModal = ({ product, isOpen = true, onClose }) => {
  const { t, isRTL } = useLanguage();
  
  if (!product) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4'
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className='bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-0'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 rounded-t-xl ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="flex-1 min-w-0 pr-2 sm:pr-4">
                  <h2 className={`text-lg sm:text-2xl font-bold text-gray-900 mb-2 line-clamp-2 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                    {product.name}
                  </h2>
                  <div className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <span className='px-2 sm:px-3 py-1 bg-primary-100 text-primary-800 text-xs sm:text-sm font-medium rounded-full'>
                      {product.category}
                    </span>
                    <span className={`text-gray-600 flex items-center gap-1 text-xs sm:text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Building2 size={14} className="sm:hidden" />
                      <Building2 size={16} className="hidden sm:block" />
                      {product.brand}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className={`p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ${isRTL ? 'ml-2 sm:ml-4' : 'mr-2 sm:mr-4'}`}
                >
                  <X size={20} className='text-gray-500 sm:hidden' />
                  <X size={24} className='text-gray-500 hidden sm:block' />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className='p-4 sm:p-6'>
              {/* Product Image */}
              <div className='mb-4 sm:mb-6'>
                <div className='aspect-w-16 aspect-h-9 bg-gray-50 rounded-lg overflow-hidden'>
                  <img
                    src={`${config.productsImagePath}/${product.image}`}
                    alt={product.name}
                    className='w-full h-48 sm:h-64 object-contain p-2 sm:p-4'
                    onError={(e) => {
                      e.target.src = config.placeholderImage;
                    }}
                  />
                </div>
              </div>

              {/* Product Information */}
              <div className='space-y-6'>
                {/* Description */}
                <div>
                  <h3 className={`text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                    <Info size={20} className='text-primary-600' />
                    {t('productDescription')}
                  </h3>
                  <p className={`text-gray-600 leading-relaxed ${isRTL ? 'text-right font-arabic' : 'text-left font-english'}`}>
                    {product.description}
                  </p>
                </div>

                {/* Details */}
                <div>
                  <h3 className={`text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse font-arabic' : 'font-english'}`}>
                    <Package size={20} className='text-primary-600' />
                    {t('productDetails')}
                  </h3>
                  <p className={`text-gray-600 leading-relaxed ${isRTL ? 'text-right font-arabic' : 'text-left font-english'}`}>
                    {product.details}
                  </p>
                </div>

                {/* Product Specifications */}
                <div className='bg-gray-50 rounded-lg p-4'>
                  <h3 className={`text-lg font-semibold text-gray-900 mb-3 ${isRTL ? 'text-right font-arabic' : 'text-left font-english'}`}>
                    {t('specifications')}
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <span className={`text-sm font-medium text-gray-700 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                        {t('brand')}:
                      </span>
                      <p className='text-gray-900'>{product.brand}</p>
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <span className={`text-sm font-medium text-gray-700 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                        {t('category')}:
                      </span>
                      <p className='text-gray-900'>{product.category}</p>
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <span className={`text-sm font-medium text-gray-700 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                        {t('productId')}:
                      </span>
                      <p className='text-gray-900'>KC-{product.id}</p>
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <span className={`text-sm font-medium text-gray-700 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                        {t('manufacturer')}:
                      </span>
                      <p className='text-gray-900'>{t('companyName')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl border-t border-gray-200 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`text-sm text-gray-500 ${isRTL ? 'font-arabic' : 'font-english'}`}>
                  {t('qualityAssured')}
                </span>
                <button
                  onClick={onClose}
                  className={`px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium ${isRTL ? 'font-arabic' : 'font-english'}`}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

ProductDetailsModal.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    description: PropTypes.string,
    details: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default ProductDetailsModal;